import { HACKLE_DEVTOOLS_STORAGE_KEY, useIsDevEnvironment } from "goi_common";

/**
 * 첫문항 실험은 니즈 x 유입 2x2 로 가른다.
 *
 * 유입으로 가르는 이유는 기저 회원가입률이 광고 7.9% 대 비광고 20.0% 로 2.5배 벌어져서다
 * (#342, #343 실측). 니즈로도 가르는 이유는 비용과 세 가지 다의 모수가 4:1 이상이라
 * 합치면 세 가지 다가 비용 결과에 묻히기 때문이다.
 *
 * 지표 분모에 니즈 이벤트를 걸어 한 실험 안에서 쪼개는 방법도 검토했으나 접었다.
 * `match_request_price_start` 는 배정 **직전** 에 찍히고, `experiment_exposure` 는 Hackle
 * SDK 가 자동으로 남기는 노출과 생성 경로가 달라(그쪽은 `useVariation` 호출 시점, 이쪽은
 * 한 틱 뒤 effect) 모수가 어긋난다. 분모를 신뢰할 수 없으면 분해도 신뢰할 수 없다.
 *
 * **키를 추측해서 적지 말 것.** Hackle 콘솔에서 만든 뒤 발급된 번호를 받아 적는다.
 * #340, #341 이 순서가 뒤집힌 채 배포된 적이 있고, 화면은 양쪽 다 정상 렌더돼 눈으로 못 잡는다.
 */
/** 첫문항 "비용" x 오가닉 */
export const QUESTION_CONTEXT_PRICE_ORGANIC_EXPERIMENT_KEY = 353;
/** 첫문항 "비용" x 광고 */
export const QUESTION_CONTEXT_PRICE_AD_EXPERIMENT_KEY = 354;
/** 첫문항 "세 가지 다" x 오가닉 */
export const QUESTION_CONTEXT_ALL_NEEDS_ORGANIC_EXPERIMENT_KEY = 356;
/** 첫문항 "세 가지 다" x 광고 */
export const QUESTION_CONTEXT_ALL_NEEDS_AD_EXPERIMENT_KEY = 357;
/** 비용 온보딩(template=cost) 단일 셀. 첫문항을 거치지 않아 니즈로 가르지 않는다 */
export const QUESTION_CONTEXT_COST_TEMPLATE_EXPERIMENT_KEY = 355;

/**
 * 다섯 실험 전부. 로그인 마킹과 devtools 오버라이드가 이 목록을 본다.
 *
 * 355 도 포함한다. 한때 355 만 로그인 화면을 그대로 두고 문항 카드 단독 효과를 재려 했으나,
 * 그러면 B 안이 진입 경로마다 다른 화면이 되어 이겨도 채택할 안이 하나로 정해지지 않는다.
 * 다섯 실험의 B 를 같은 화면으로 고정했다.
 *
 * **대가**: 문항 카드 효과와 로그인 후기 효과를 가르는 셀이 사라졌다. 결과가 양수여도
 * 어느 쪽이 끌었는지는 이 실험들만으로 못 가린다.
 */
export const QUESTION_CONTEXT_EXPERIMENT_KEYS = [
  QUESTION_CONTEXT_PRICE_ORGANIC_EXPERIMENT_KEY,
  QUESTION_CONTEXT_PRICE_AD_EXPERIMENT_KEY,
  QUESTION_CONTEXT_ALL_NEEDS_ORGANIC_EXPERIMENT_KEY,
  QUESTION_CONTEXT_ALL_NEEDS_AD_EXPERIMENT_KEY,
  QUESTION_CONTEXT_COST_TEMPLATE_EXPERIMENT_KEY,
] as const;

/**
 * 첫문항 답변과 진입 유입으로 실험 키를 고른다.
 *
 * @param needs 첫문항에서 고른 니즈. 비용과 세 가지 다만 이 실험 모수다
 * @param isAdEntry `hasAdEntryParams` 판정 (n_query / n_ad / gclid)
 */
export const resolveQuestionContextExperimentKey = ({
  needs,
  isAdEntry,
}: {
  needs: "price" | "priceAndProcedure";
  isAdEntry: boolean;
}): number => {
  if (needs === "price") {
    return isAdEntry
      ? QUESTION_CONTEXT_PRICE_AD_EXPERIMENT_KEY
      : QUESTION_CONTEXT_PRICE_ORGANIC_EXPERIMENT_KEY;
  }

  return isAdEntry
    ? QUESTION_CONTEXT_ALL_NEEDS_AD_EXPERIMENT_KEY
    : QUESTION_CONTEXT_ALL_NEEDS_ORGANIC_EXPERIMENT_KEY;
};

export type QuestionContextVariation = "A" | "B";

const QUESTION_CONTEXT_LOGIN_VARIATION_KEY = "question_context_login_variation";

export const isQuestionContextTreatment = (
  variation: string | null | undefined,
): variation is Exclude<QuestionContextVariation, "A"> => variation === "B";

/**
 * B 만 로그인 화면까지 처치를 잇는다. 다섯 실험이 이 마킹을 공유한다.
 *
 * 355(template=cost)도 포함한다. 그 경로는 첫문항을 안 거쳐 `getMatchRequestNeeds()`
 * 마킹이 없고 로그인이 `CostSavingsMessage` 로 흘렀는데, B 에서 `needs="price"` 를 넘겨
 * 마킹을 심고 다른 넷과 같은 화면을 보게 한다(`MatchRequestOnboardingFlow` 참고).
 */
export const markQuestionContextLoginVariation = (variation: QuestionContextVariation) => {
  if (!isQuestionContextTreatment(variation) || typeof window === "undefined") return;

  try {
    sessionStorage.setItem(QUESTION_CONTEXT_LOGIN_VARIATION_KEY, variation);
  } catch {
    // storage를 쓸 수 없으면 로그인 화면은 기존 흐름(A)으로 안전하게 떨어진다.
  }
};

const getForcedQuestionContextVariation = (): QuestionContextVariation | null => {
  try {
    const raw = window.localStorage.getItem(HACKLE_DEVTOOLS_STORAGE_KEY);
    if (!raw) return null;

    const variations: Record<string, string> = JSON.parse(raw);
    const forced = QUESTION_CONTEXT_EXPERIMENT_KEYS.map((key) => variations[key]).find(
      (variation) => variation === "A" || variation === "B",
    );

    // A 도 그대로 돌려준다. "B 가 아니면 null" 로 두면 A 를 강제했을 때 호출부가
    // 세션 마킹으로 폴백해, 직전 테스트에서 남은 B 마킹 때문에 A 인데 B 화면이 나온다.
    // (costNeedsResultScreen 의 devToolsForcedB 도 A 를 인식한다)
    return (forced as QuestionContextVariation | undefined) ?? null;
  } catch {
    return null;
  }
};

/** 로그인 B 후기 카드 대상. dev에서는 강제 variation이 세션 마킹보다 우선한다. */
export const useQuestionContextLoginVariation = (): QuestionContextVariation | null => {
  const isDevEnvironment = useIsDevEnvironment();
  if (typeof window === "undefined") return null;

  if (isDevEnvironment) {
    const forced = getForcedQuestionContextVariation();
    if (forced !== null) return forced;
  }

  try {
    const variation = sessionStorage.getItem(QUESTION_CONTEXT_LOGIN_VARIATION_KEY);
    return variation === "B" ? variation : null;
  } catch {
    return null;
  }
};
